import React from 'react';
import type { Session } from 'state';

import { formatters } from 'services';
import PriceCell from 'reusable/PriceCell';

import InstrumentActions from './InstrumentActions';
import { ordinaryQuoteIsAvailable } from './model';
import type { MarketInstrument, SelectInstrument } from './types';

type Props = {
  rows: MarketInstrument[];
  session: Session;
  reportingCurrencySymbol: string;
  selectedInstrumentId?: number;
  selectInstrument: SelectInstrument;
  showOrderModal: () => void;
  showODAModal: () => void;
};

const pnlClass = (value: number) =>
  value > 0 ? 'is-positive' : value < 0 ? 'is-negative' : '';

const BondsMarket: React.FC<Props> = ({
  rows,
  session,
  reportingCurrencySymbol,
  selectedInstrumentId,
  selectInstrument,
  showOrderModal,
  showODAModal,
}) => (
  <div className="markets-v2-bond-scroll">
    <table className="markets-v2-bond-table">
      <thead>
        <tr>
          <th>Instrument</th>
          <th>Availability</th>
          <th className="is-number">Term / duration</th>
          <th className="is-number">Coupon</th>
          <th className="is-number">Yield</th>
          <th className="is-number">Two-way price</th>
          <th className="is-number">Position</th>
          <th className="is-number">DV01</th>
          <th className="is-number">P&amp;L · {reportingCurrencySymbol}</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(row => {
          const quoted = ordinaryQuoteIsAvailable(row.asset);
          const scheduled =
            !quoted &&
            Boolean(row.asset.asset_issue_date) &&
            new Date(row.asset.asset_issue_date).getTime() >
              new Date(session.scenario_date).getTime();
          const selected = selectedInstrumentId === row.instrument.id;
          const focus = () => selectInstrument(row.instrument);

          return (
            <tr
              key={row.instrument.id}
              className={`${selected ? 'is-selected' : ''}${
                quoted ? '' : ' is-upcoming'
              }`}
              onClick={focus}
            >
              <td>
                <div className="markets-v2-bond-name">
                  <strong title={row.asset.name}>
                    {row.asset.domicile || row.asset.geography || 'Global'} Govt
                    · {formatters.commasExact3Digits(row.asset.coupon)}% ·{' '}
                    {formatters.commas2Digits(
                      row.instrument.htm_afs_years_to_maturity,
                    )}
                    Y
                  </strong>
                  <span title={row.asset.ticker}>{row.asset.ticker}</span>
                  <small>
                    {row.asset.issuer_type || 'Govt.'} ·{' '}
                    {row.asset.domicile || row.asset.geography}
                  </small>
                </div>
              </td>
              <td>
                <span
                  className={`markets-v2-quote-state ${
                    quoted ? 'is-live' : 'is-waiting'
                  }`}
                >
                  <i />
                  {quoted
                    ? 'Live now'
                    : scheduled
                      ? 'Future vintage'
                      : 'Quote unavailable'}
                </span>
                {!quoted && (
                  <span className="markets-v2-cell-note">
                    {scheduled
                      ? 'Visible now · tradable once issued'
                      : 'Visible now · waiting for a usable quote'}
                  </span>
                )}
              </td>
              <td className="is-number">
                <strong>
                  {formatters.commas2Digits(
                    row.instrument.htm_afs_years_to_maturity,
                  )}{' '}
                  yrs
                </strong>
                <span>
                  {formatters.commasExact3Digits(
                    row.instrument.htm_afs_duration,
                  )}{' '}
                  duration
                </span>
              </td>
              <td className="is-number">
                <strong>
                  {formatters.commasExact3Digits(row.asset.coupon)}%
                </strong>
                <span>
                  {row.instrument.htm_afs_days_to_coupon || '—'} days to coupon
                </span>
              </td>
              <td className="is-number">
                <strong>
                  {quoted
                    ? `${formatters.commasExact3Digits(row.asset.ask_yield)}%`
                    : '—'}
                </strong>
              </td>
              <td className="is-number">
                {quoted ? (
                  <div className="markets-v2-bond-quote">
                    <span>
                      Bid{' '}
                      <strong>
                        <PriceCell
                          price={formatters.priceFormat(
                            row.asset.bid,
                            row.asset.ticker,
                            row.currencySymbol,
                            row.asset.resourcetype,
                          )}
                          priceDiff={row.asset.bid_diff}
                        />
                      </strong>
                    </span>
                    <span>
                      Ask{' '}
                      <strong>
                        <PriceCell
                          price={formatters.priceFormat(
                            row.asset.ask,
                            row.asset.ticker,
                            row.currencySymbol,
                            row.asset.resourcetype,
                          )}
                          priceDiff={row.asset.ask_diff}
                        />
                      </strong>
                    </span>
                  </div>
                ) : (
                  <span className="markets-v2-muted-dash">— / —</span>
                )}
              </td>
              <td className="is-number">
                <strong>
                  {formatters.commasNoDigits(row.instrument.amount)}
                </strong>
                <span>
                  {formatters.commasNoDigits(row.reportingValue)}{' '}
                  {reportingCurrencySymbol}
                </span>
              </td>
              <td className="is-number">
                <strong>
                  {formatters.commas2Digits(row.instrument.htm_afs_dv01)}
                </strong>
              </td>
              <td className="is-number">
                <strong
                  className={pnlClass(row.instrument.reporting_unrealized_pnl)}
                >
                  {formatters.commasNoDigits(
                    row.instrument.reporting_unrealized_pnl,
                  )}
                </strong>
                <span>
                  {formatters.commasNoDigits(
                    row.instrument.reporting_realized_pnl,
                  )}{' '}
                  realized
                </span>
              </td>
              <td>
                <InstrumentActions
                  row={row}
                  session={session}
                  selected={selected}
                  onFocus={focus}
                  onTrade={() => {
                    focus();
                    showOrderModal();
                  }}
                  onODA={() => {
                    focus();
                    showODAModal();
                  }}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export default BondsMarket;
